/**
 * Phaser DOM helpers for the menu scenes. The slice-1 screens (Splash, Room
 * Browser) are parchment/wood panels with inputs, scroll lists, and modals —
 * faithfully reproducing the prototype's gradients, drop-shadows, and form
 * controls as Phaser GameObjects would be far more code than value, so we
 * render them as a single DOM Element overlaid on the canvas (the playbook's
 * "DOM overlay via the Phaser DOM container" option). One 880x430 `.pp-root`
 * div per scene, top-left anchored over the FIT-scaled canvas.
 */

import Phaser from 'phaser';

/**
 * Create the scene's root DOM element (880x430 `.pp-root`), origin top-left so
 * it lines up exactly with the FIT-scaled Phaser canvas. Returns the wrapping
 * `HTMLElement` so the scene can query/bind to children.
 */
export function mountRoot(scene: Phaser.Scene, innerHtml: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'pp-root';
  el.innerHTML = innerHtml;
  scene.add.dom(0, 0, el).setOrigin(0, 0);
  return el;
}

/** Show a transient parchment toast inside the scene root (auto-removes). */
export function showToast(root: HTMLElement, text: string, ms = 1800): void {
  const prev = root.querySelector('.toast');
  if (prev) prev.remove();
  const t = document.createElement('div');
  t.className = 'toast';
  t.textContent = text;
  root.appendChild(t);
  window.setTimeout(() => t.remove(), ms);
}

/**
 * Minimal HTML-escape for any user/server-sourced string injected into
 * `innerHTML` (room names, host names, codes, chat). LIVE data in slice 2 is
 * untrusted, so every interpolated value must pass through here.
 */
export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&':
        return '&amp;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      case '"':
        return '&quot;';
      default:
        return '&#39;';
    }
  });
}

/**
 * Open at most one modal per scene root at a time (re-entrancy guard): if a
 * `.modal-bg` is already mounted, returns false and the caller should no-op.
 * Prevents a double-click / double-binding from stacking duplicate modals.
 */
export function canOpenModal(root: HTMLElement): boolean {
  return root.querySelector('.modal-bg') === null;
}
